import React from 'react'
import { WIZARD_SCREEN_TYPES } from '../../constants'
import IntroView from '../../components/IntroView'
import CallToActionView from '../../components/CallToActionView'
import BathtubSparkle from '../../../../assets/bathtub-sparkle.png'
import CurrentWeight from './CurrentWeight'
import SheddingAmount from './SheddingAmount'
import SheddingFrequency from './SheddingFrequency'
import BreedMix from './BreedMix'
import SingleBreed from './SingleBreed'
import DoodleMix from './DoodleMix'
import PhotoSubmission from './PhotoSubmission'
import ComboMix from './ComboMix'
import DoodleCoatType from './DoodleCoatType'
import CoatType from './CoatType'
import EarCleaning from './EarCleaning'
import NailTrimming from './NailTrimming'
import Glands from './Glands'
import ClientNotes from './ClientNotes'
import { GROOMING_PROFILE_KEYS, BREED_MIX_TYPES, SHEDDING_AMOUNT_TYPES } from './constants'
import { ENTRY_POINTS } from '.'

export const PHOTO_SUBMISSION_ATTRIBUTES = {
	reference_photo_email_sent: 'reference_photo_email_sent'
}
export const SECTION_IDS = {
	basic: 'basic',
	details: 'details',
	preferences: 'preferences'
}
export const SECTIONS = {
	[SECTION_IDS.basic]: {
		sectionName: 'Section 1',
		sectionTitle: 'Basic Information',
		sectionDescription: `Let's begin by collecting information on the dog's weight to start setting up their profile.`
	},
	[SECTION_IDS.details]: {
		sectionName: 'Section 2',
		sectionTitle: 'Coat Details',
		sectionDescription: `Next, we'll collect information about their coat, including shed patterns, breed, fur comparisons, and more.`
	},
	[SECTION_IDS.preferences]: {
		sectionName: 'Section 3',
		sectionTitle: 'Groom Preferences',
		sectionDescription: `All Skiptown grooming services include ear cleaning, nail trimmer, and anal gland expression (as needed). Let's set them up!`
	}
}

export const WORKFLOW = [
	{
		type: WIZARD_SCREEN_TYPES.Start,
		component: (props = {}) => (
			<CallToActionView
				mainText={
					props?.from === ENTRY_POINTS.create_appointment
						? `Ruh-roh! We're missing a grooming profile for ${props?.petName || '?SOMEDOGGO?'}.`
						: `Hi ${props?.userName}, let's setup a grooming profile!`
				}
				secondaryText={
					props?.from === ENTRY_POINTS.create_appointment
						? `Before booking an appointment, set up a grooming profile for ${
								props?.petName || '?SOMEDOGGO?'
						  }. This will only need to be filled out once.`
						: `Click 'Get Started' to begin setting up a grooming profile for ${
								props?.petName || '?SOMEDOGGO?'
						  }.`
				}
				imageSrc={BathtubSparkle}
				actionTitle="Get Started"
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.basic,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.basic]} {...props} />
	},
	{
		section: SECTION_IDS.basic,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.weight,
		requires_api_update: () => true,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.weight] > '',
		component: (props = {}) => (
			<CurrentWeight title={`What's ${props?.petName}'s current weight?`} {...props} />
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.details]} {...props} />
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.shedding_amount,
		requires_api_update: (state) =>
			state?.[GROOMING_PROFILE_KEYS.shedding_amount] === SHEDDING_AMOUNT_TYPES.none,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.shedding_amount] > '',
		component: (props = {}) => <SheddingAmount title={`How much does ${props?.petName} shed?`} {...props} />
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.shedding_frequency,
		requires_api_update: () => true,
		display_step_condition: (state) => {
			return state?.[GROOMING_PROFILE_KEYS.shedding_amount] !== SHEDDING_AMOUNT_TYPES.none
		},
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.shedding_frequency] > '',
		component: (props = {}) => (
			<SheddingFrequency
				title={`How does ${props?.petName}'s shedding change throughout the year?`}
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breed_mix,
		requires_api_update: () => true,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.breed_mix] > '',
		component: (props = {}) => <BreedMix title={`What breed mix is ${props?.petName}?`} {...props} />
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breeds,
		requires_api_update: () => true,
		display_step_condition: (state) =>
			state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.single ||
			state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.single_suspected,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.breeds].length > 0,
		component: (props = {}) => <SingleBreed title={`What breed is ${props?.petName}?`} {...props} />
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breeds,
		requires_api_update: () => true,
		display_step_condition: (state) => state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.doodle,
		can_proceed_condition: (state) =>
			state?.[GROOMING_PROFILE_KEYS.breeds][0] > '' &&
			state?.[GROOMING_PROFILE_KEYS.breeds][1] !== undefined &&
			state?.[GROOMING_PROFILE_KEYS.breeds][1] === 'Poodle Mix',
		component: (props = {}) => (
			<DoodleMix title={`${props?.petName} is a mix of Poodle and what other breed?`} {...props} />
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breeds,
		requires_api_update: () => false,
		display_step_condition: (state) => false, // state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.doodle,
		can_proceed_condition: (state) => true, //state?.[GROOMING_PROFILE_KEYS.reference_photo_email_sent] === true,
		component: (props = {}) => (
			<PhotoSubmission
				title="Which Doodle coat below most closely resembles your dog's coat?"
				subtext="Select the coat type that best resembles your dog's coat on the form we emailed you to help your groomer."
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.coat_type,
		requires_api_update: () => true,
		display_step_condition: (state) => state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.doodle,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.coat_type] > '',
		component: (props = {}) => (
			<DoodleCoatType
				title="Select the answer that the client states matches their dog's coat type."
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breeds,
		requires_api_update: () => true,
		display_step_condition: (state) => state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.combo,
		can_proceed_condition: (state) =>
			state?.[GROOMING_PROFILE_KEYS.breeds][0] > '' &&
			state?.[GROOMING_PROFILE_KEYS.breeds][1] !== undefined &&
			state?.[GROOMING_PROFILE_KEYS.breeds][1] > '',
		component: (props = {}) => <ComboMix title={`What are ${props?.petName}'s two breeds?`} {...props} />
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.breeds,
		requires_api_update: () => false,
		display_step_condition: (state) => {
			return false
			// state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.combo ||
			// state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.unknown
		},
		can_proceed_condition: (state) => true, //state?.[GROOMING_PROFILE_KEYS.reference_photo_email_sent] === true,
		component: (props = { petName: 'Your dog' }) => (
			<PhotoSubmission
				title={`Which dog coat most closely resembles ${props?.petName}'s coat?`}
				subtext="Select the coat type that best resembles your dog's coat on the form we emailed you to help your groomer."
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.details,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.coat_type,
		requires_api_update: () => true,
		display_step_condition: (state) =>
			state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.combo ||
			state?.[GROOMING_PROFILE_KEYS.breed_mix] === BREED_MIX_TYPES.unknown,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.coat_type] > '',
		component: (props = {}) => (
			<CoatType title="Select the answer that the client states matches their dog's coat type." {...props} />
		)
	},
	{
		section: SECTION_IDS.preferences,
		type: WIZARD_SCREEN_TYPES.Intro,
		can_proceed_condition: () => true,
		component: (props = {}) => <IntroView {...SECTIONS[SECTION_IDS.preferences]} {...props} />
	},
	{
		section: SECTION_IDS.preferences,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.ear_cleaning,
		requires_api_update: () => true,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.ear_cleaning] > '',
		component: (props = {}) => (
			<EarCleaning
				title={`Please select your ear cleaning setting for ${props?.petName}'s grooming services.`}
				subtext={`Standard ear cleaning includes anti-bacterial ear powder and wiping the visible part of the ear canal clean.`}
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.preferences,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.nail_trimming,
		requires_api_update: () => true,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.nail_trimming] > '',
		component: (props = {}) => (
			<NailTrimming
				title={`Please select your nail trimming setting for ${props?.petName}'s grooming services.`}
				subtext={`Nail trimming includes nail clipping and/or nail filing depending on services selected during booking.`}
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.preferences,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.glands,
		requires_api_update: () => true,
		can_proceed_condition: (state) => state?.[GROOMING_PROFILE_KEYS.glands] > '',
		component: (props = {}) => (
			<Glands
				title={`Please select your anal gland expression setting for ${props?.petName}'s grooming services.`}
				subtext={`Our recommendation is to express anal glands as needed.`}
				{...props}
			/>
		)
	},
	{
		section: SECTION_IDS.preferences,
		type: WIZARD_SCREEN_TYPES.Form,
		attr: GROOMING_PROFILE_KEYS.client_grooming_notes,
		requires_api_update: () => true,
		can_proceed_condition: () => true,
		component: (props = {}) => (
			<ClientNotes
				title={`Anything we need to know about how ${props?.petName} behaves in a grooming environment?`}
				{...props}
			/>
		)
	},
	{
		type: WIZARD_SCREEN_TYPES.Exit,
		component: (props = {}) => (
			<CallToActionView
				mainText={`You did it! You set up a grooming profile for ${props?.petName || '?SOMEDOGGO?'}.`}
				secondaryText={
					props?.from === ENTRY_POINTS.create_appointment
						? `Click continue to start booking a grooming appointment for ${
								props?.petName || '?SOMEDOGGO?'
						  }`
						: 'You can either exit to the pet profile or create a new appointment.'
				}
				imageSrc={BathtubSparkle}
				actionTitle={
					props?.from === ENTRY_POINTS.create_appointment
						? 'Continue to booking wizard'
						: 'Exit to pet profile'
				}
				secondaryActionTitle={props?.from === ENTRY_POINTS.pet ? 'Create new appointment' : ''}
				{...props}
			/>
		)
	}
]
